/**
 * Brand scope for admin lists and figures.
 *
 * `programs.brand_key` is the only brand column; an enrollment, invoice or payment takes its
 * program's brand. Admin screens read that value off rows that may predate it or embed a
 * program without it, so every comparison goes through `brandKeyOf`, which reads a missing or
 * unrecognized value as the default brand -- the same rule as the column's DB default.
 *
 * Presentation and reporting scope only. Nothing here is an access rule.
 */

import { BRAND_KEYS, DEFAULT_BRAND_KEY, isBrandKey, type BrandKey } from '../../config/brands';
import { BRAND_PROFILES } from '../../config/brandProfiles';

/** What an admin brand picker can hold: one brand, or every brand. */
export type BrandFilter = BrandKey | 'all';

/** Every value a brand picker can hold, for URL-backed state. */
export const BRAND_FILTER_VALUES: readonly BrandFilter[] = ['all', ...BRAND_KEYS];

/** A row's brand, with a missing or unknown value read as the default brand. */
export function brandKeyOf(value: unknown): BrandKey {
  return isBrandKey(value) ? value : DEFAULT_BRAND_KEY;
}

/** Whether a row with this brand belongs in a list filtered to `filter`. */
export function matchesBrandFilter(value: unknown, filter: BrandFilter): boolean {
  return filter === 'all' || brandKeyOf(value) === filter;
}

/**
 * " (Kaleido)" after a name, for places a tag component cannot go -- an `<option>`, a confirm
 * dialog. Empty for the default brand, matching `AdminBrandTag`.
 */
export function brandNameSuffix(value: unknown): string {
  const brand = brandKeyOf(value);
  return brand === DEFAULT_BRAND_KEY ? '' : ` (${BRAND_PROFILES[brand].shortName})`;
}

/** A `?brand=` URL value as a filter; anything else is every brand. */
export function parseBrandFilter(value: string | null | undefined): BrandFilter {
  return isBrandKey(value) ? value : 'all';
}

/**
 * The value to send as an RPC's `p_brand`, or `undefined` to leave the argument out.
 *
 * Left out rather than sent as null on purpose: a call without the argument is exactly the call
 * this page made before the parameter existed, so the default "all brands" view keeps working
 * against a database the brand patch has not reached yet.
 */
export function brandRpcArg(filter: BrandFilter): { p_brand?: BrandKey } {
  return filter === 'all' ? {} : { p_brand: filter };
}

/**
 * Split a list of rows into one bucket per brand, in `BRAND_KEYS` order, dropping brands with
 * no rows. For figures that must never be summed across brands.
 */
export function groupByBrand<T>(rows: readonly T[], brandOf: (row: T) => unknown): { brand: BrandKey; rows: T[] }[] {
  const buckets = new Map<BrandKey, T[]>();
  for (const row of rows) {
    const brand = brandKeyOf(brandOf(row));
    const bucket = buckets.get(brand);
    if (bucket) bucket.push(row);
    else buckets.set(brand, [row]);
  }
  return BRAND_KEYS.filter((brand) => buckets.has(brand)).map((brand) => ({ brand, rows: buckets.get(brand) ?? [] }));
}
